import React from "react";
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ContentItem } from "../types";
import { getDisplayDaySincePublished } from "../utils/dailyMetricsDisplayDay";

interface EpisodeMetricsPanelProps {
  contentItem: ContentItem;
}

const EpisodeMetricsPanel: React.FC<EpisodeMetricsPanelProps> = ({
  contentItem,
}) => {
  if (!contentItem.dailyMetrics || contentItem.dailyMetrics.length === 0) {
    return null;
  }

  const sortedMetrics = [...contentItem.dailyMetrics].sort(
    (a, b) =>
      new Date(a.collectedAt).getTime() - new Date(b.collectedAt).getTime(),
  );
  const selectedDate = contentItem.selectedMetricDate;

  return (
    <TableContainer
      sx={{
        maxHeight: 300,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Day</strong>
              </TableCell>
              <TableCell>
                <strong>Date</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Views</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Likes</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Comments</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Δ Views</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedMetrics.map((metric, idx, arr) => {
              const prevMetric = idx > 0 ? arr[idx - 1] : null;
              const viewsDelta = prevMetric
                ? metric.views - prevMetric.views
                : metric.views;

              const daysSincePublished = getDisplayDaySincePublished(
                contentItem.publishedAt,
                metric.collectedAt,
                metric.daysSincePublished,
              );

              const utcDate = new Date(metric.collectedAt);
              const chileanDate = new Date(
                utcDate.getTime() - 3 * 60 * 60 * 1000,
              );

              const day = String(chileanDate.getUTCDate()).padStart(2, "0");
              const month = String(chileanDate.getUTCMonth() + 1).padStart(
                2,
                "0",
              );
              const year = chileanDate.getUTCFullYear();
              const chileanDateStr = `${day}/${month}/${year}`;

              const chileanTimeStr = chileanDate.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone: "UTC",
              });

              const isSelectedMetric =
                selectedDate && metric.collectedAt === selectedDate;

              return (
                <TableRow
                  key={metric.collectedAt}
                  sx={{
                    "&:hover": { bgcolor: "action.hover" },
                    bgcolor: isSelectedMetric ? "success.50" : "inherit",
                  }}
                >
                  <TableCell>
                    <Chip
                      label={`Day ${daysSincePublished}`}
                      size="small"
                      color={isSelectedMetric ? "success" : "default"}
                      sx={{ fontWeight: "bold", minWidth: 60 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{chileanDateStr}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {chileanTimeStr} CLT
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {metric.views.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {metric.likes.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {metric.comments.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={
                        viewsDelta > 0 ? "success.main" : "text.secondary"
                      }
                      fontWeight={viewsDelta > 0 ? "bold" : "normal"}
                    >
                      {viewsDelta > 0 ? "+" : ""}
                      {viewsDelta.toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
    </TableContainer>
  );
};

export default EpisodeMetricsPanel;
